import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ControlValueAccessor,
} from '@angular/forms';
import { LocationService } from '../../services';
import { ComboSelectComponent } from '../combo-select/combo-select.component';
import { Address, Coordinates, Geolocation } from '@seech/shared-logic-ng';

// eslint-disable-next-line no-var
declare var google: any;

@Component({
  standalone: true,
  selector: 'seech-location',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ComboSelectComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LocationComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => LocationComponent), multi: true },
  ],
})
export class LocationComponent
  implements OnChanges, ControlValueAccessor
{
  @ViewChild('locationInput', { static: true }) locationInput!: ComboSelectComponent;

  @Input() mapEnabled = false;
  @Input() autoSelect = true;
  @Input() chipsColor = 'info';
  @Input() results: any[] = [];
  @Input() value: string | string[] | any = '';
  @Input() id: string | number | null | undefined;
  @Input() placeholder = 'Enter a location';
  @Input() hideBorder = false;
  @Input() maxRowCount = 2;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() label?: string;
  @Input() coordinates!: Coordinates | Coordinates[];
  @Input() maxSelectionCount = 1;
  @Input() emptyStateText = 'No location selected';

  @Output() placeSelected = new EventEmitter<Geolocation>();
  @Output() selectionLimitReached = new EventEmitter<boolean>();
  @Output() valuesChange = new EventEmitter<any[]>();

  private formattedLocation!: Coordinates;
  private address: Address = {};
  private autocompleteInitialized = false;
  private geocodeCache = new Map<string, Address>();
  private processedLocations = new Set<string>();
  private onChange = (value: boolean) => {};
  private onTouched = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private locationService: LocationService
  ) {
    this.locationService
      .load(this.mapEnabled)
      .then(() => {
        if (!this.autocompleteInitialized) {
          this.initializeAutocomplete();
        }
      })
      .catch((error) => {
        console.error('Error loading Maps API:', error);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['coordinates'] && changes['coordinates'].currentValue) {
      const coords = changes['coordinates'].currentValue;
      const newCoordinates = JSON.stringify(coords);
      if (!this.processedLocations.has(newCoordinates)) {
        this.processedLocations.add(newCoordinates);
        Array.isArray(coords) ? coords.forEach(this.processCoordinate.bind(this)) : this.processCoordinate(coords);
      }
    }
  }

  private initializeAutocomplete() {
    const comboInput = this.locationInput.comboInput();
    if (comboInput) {
      const autocomplete = new google.maps.places.Autocomplete(
        comboInput.nativeElement
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          this.handlePlaceSelection(place);
        }
      });
    }
    this.autocompleteInitialized = true;
  }

  private handlePlaceSelection(place: any) {
    this.formattedLocation = {
      location: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      coords: `${place.geometry.location.lat()}, ${place.geometry.location.lng()}`,
    };

    this.address = this.extractAddress(place.address_components);
    this.value = this.formattedLocation.location;
    this.cdr.detectChanges();

    this.placeSelected.emit({ coordinates: this.formattedLocation, address: this.address });

    if (this.autoSelect) {
      this.triggerEnterKey();
    }
  }

  private extractAddress(components: any[]): Address {
    const address: Address = {};
    components.forEach((component) => {
      if (component.types.includes('street_number')) address.street = component.long_name;
      else if (component.types.includes('route')) address.street = `${address.street || ''} ${component.long_name}`;
      else if (component.types.includes('locality')) address.city = component.long_name;
      else if (component.types.includes('administrative_area_level_1')) address.state = component.long_name;
      else if (component.types.includes('country')) {
        address.country = component.long_name;
        address.countryCode = component.short_name;
      } else if (component.types.includes('postal_code')) address.postalCode = component.long_name;
    });
    return address;
  }

  private getAddress(coords: Coordinates): void {
    const cacheKey = `${coords.latitude},${coords.longitude}`;
    if (this.geocodeCache.has(cacheKey)) {
      this.emitPlaceSelected(coords, this.geocodeCache.get(cacheKey)!);
      return;
    }

    new google.maps.Geocoder().geocode({ location: new google.maps.LatLng(coords.latitude, coords.longitude) }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const extractedAddress = this.extractAddress(results[0].address_components);
        this.geocodeCache.set(cacheKey, extractedAddress);
        this.emitPlaceSelected(coords, extractedAddress);
      }
    });
  }

  private emitPlaceSelected(coords: Coordinates, address: Address) {
    this.placeSelected.emit({ coordinates: { ...coords, location: address.street || '' }, address });
  }

  getCurrentLocation() {
    this.locationService.getCurrentLocation(true).subscribe(
      (coords) => {
        const coordKey = `${coords.latitude},${coords.longitude}`;
        if (!this.processedLocations.has(coordKey)) {
          this.processedLocations.add(coordKey);
          this.getAddress(coords);
        }
      },
      (error) => console.error('Failed to get location:', error)
    );
  }

  private triggerEnterKey() {
    const event = new KeyboardEvent('keyup', {
      bubbles: true,
      cancelable: true,
      key: 'Enter',
      code: 'Enter',
    });
    this.locationInput.comboInput()!.nativeElement.dispatchEvent(event);
  }

  onSelectionLimitReached(event: boolean) {
    this.selectionLimitReached.emit(event);
  }

  private async processCoordinate(coord: Coordinates): Promise<void> {
    const coordKey = `${coord.latitude},${coord.longitude}`;
    if (!this.geocodeCache.has(coordKey)) {
      await this.getAddress(coord);
    }
  }

  // private async processCoordinate(coord: Coordinates): Promise<void> {
  //   const coordKey = `${coord.latitude},${coord.longitude}`;

  //   // Check if service cache already has the data
  //   if (this.locationService.geocodeCache.has(coordKey)) {
  //     this.placeSelected.emit({
  //       coordinates: coord,
  //       address: this.locationService.geocodeCache.get(coordKey)!,
  //     });
  //     return;
  //   }

  //   // If not in cache, fetch from API
  //   await this.getAddress(coord);
  // }

  emitChanges(event: any) {
    this.valuesChange.emit(event);
  }

  writeValue(value: any): void {
    if (!value) {
      this.value = '';
      this.locationInput.inputValue = '';
      return;
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        this.value = value.map((coord: Coordinates) => coord.location || '').join(', ');
        value.forEach((coord) => {
          const coordKey = `${coord.latitude},${coord.longitude}`;
          if (!this.processedLocations.has(coordKey)) {
            this.processedLocations.add(coordKey);
            this.processCoordinate(coord);
          }
        });
      } else if (value.latitude && value.longitude) {
        const coordKey = `${value.latitude},${value.longitude}`;
        if (!this.processedLocations.has(coordKey)) {
          this.processedLocations.add(coordKey);
          this.processCoordinate(value);
        }
      }
    }

    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
